<template>
  <div>
    <page-header has-return :page="page" @onReturn="onReturn" />
    <div class="row">
      <div class="col-12">
        <div class="erp-search-bar d-flex align-items-center">
          <div class="erp-search-bar__field">
            <custom-drop-down
              v-model="filters.operation"
              fieldkey="id"
              fieldname="name"
              :options="operations"
              placeholder="Operación"
            />
          </div>
          <div class="erp-search-bar__field">
            <custom-date-range-picker v-model="dateRange" />
          </div>
          <div class="erp-search-bar__field erp-search-bar__field--input">
            <i class="fas fa-search erp-search-bar__icon" />
            <input
              v-model="filters.correlationId"
              class="form-control erp-search-bar__control"
              placeholder="Correlation Id"
              type="text"
              @keyup.enter="search"
            />
          </div>
          <div class="erp-search-bar__action">
            <action-button
              class="erp-search-bar__btn"
              i="fas fa-search"
              variant="success"
              @click="search"
            >
              Buscar
            </action-button>
          </div>
        </div>
      </div>
    </div>
    <custom-title-divider title="Resultados" />

    <div v-if="results.length" class="erp-results">
      <div
        v-for="item in results"
        :key="item.id"
        class="mb-3"
        :class="{ 'erp-item--banner': item.message }"
      >
        <callout
          border-color="celadon"
          controlled
          :opened="isExpanded(item.id)"
          @click:open="toggleSteps(item.id)"
        >
          <div class="row p-3 align-items-center">
            <div class="col">
              <span class="data-label">ID:</span>
              <p class="data mb-0">{{ item.id }}</p>
            </div>
            <div class="col">
              <span class="data-label">Correlation ID:</span>
              <p class="data mb-0">{{ item.correlationId }}</p>
            </div>
            <div class="col">
              <span class="data-label">Operación:</span>
              <p class="data mb-0">{{ item.operationName }}</p>
            </div>
            <div class="col">
              <span class="data-label">Inicio:</span>
              <p class="data mb-0">{{ formatDate(item.startedAt) }}</p>
            </div>
            <div class="col">
              <span class="data-label">Duración:</span>
              <p class="data mb-0">{{ formatDuration(item.durationMs) }}</p>
            </div>
            <div class="col-auto d-flex align-items-center">
              <action-button
                i="fas fa-list-ul"
                variant="outline-success"
                @click="toggleSteps(item.id)"
              >
                Ver Pasos
              </action-button>
            </div>
          </div>
        </callout>
        <div v-if="item.message" class="erp-message px-3 py-2">
          <span class="data-label">Mensaje:</span>
          <span class="data ml-1">{{ item.message }}</span>
        </div>

        <div v-if="isExpanded(item.id)" class="erp-steps">
          <div class="erp-steps__header">
            <span class="erp-steps__badge">
              <em class="fas fa-list-ul" />
            </span>
            <span>Pasos de la Operación</span>
          </div>

          <div v-if="isLoadingSteps(item.id)" class="text-center py-3">
            <em class="fas fa-spinner fa-spin mr-2" />
            Cargando pasos...
          </div>
          <div
            v-else-if="!stepsFor(item.id).length"
            class="text-center py-3 color-secondary-6"
          >
            No hay pasos registrados para esta operación.
          </div>

          <div
            v-for="step in stepsFor(item.id)"
            :key="step.id"
            class="erp-step"
            :class="{ 'is-open': isStepOpen(step.id) }"
          >
            <button
              class="erp-step__title"
              type="button"
              @click="toggleStep(step.id)"
            >
              <span class="erp-step__chevron">
                <em class="fas fa-chevron-down" />
              </span>
              <span class="erp-step__paso">PASO {{ step.stepOrder }}:</span>
              <span class="erp-step__name">
                {{ formatStepName(step.stepName) }}
              </span>
            </button>
            <transition
              name="erp-collapse"
              @after-enter="collapseAfterEnter"
              @before-enter="collapseBeforeEnter"
              @before-leave="collapseBeforeLeave"
              @enter="collapseEnter"
              @leave="collapseLeave"
            >
              <div v-if="isStepOpen(step.id)" class="erp-step__body">
                <div class="erp-payload">
                  <div class="erp-payload__head">
                    <span class="erp-payload__label">
                      <em class="fas fa-file-import" />
                      Input Payload
                    </span>
                    <action-button
                      v-if="step.inputPayload"
                      class="erp-copy-btn"
                      :class="{ 'is-copied': copiedKey === 'in-' + step.id }"
                      :i="
                        copiedKey === 'in-' + step.id
                          ? 'fas fa-check'
                          : 'fas fa-copy'
                      "
                      :variant="
                        copiedKey === 'in-' + step.id
                          ? 'success'
                          : 'outline-success'
                      "
                      @click="
                        copyText(
                          formatPayload(step.inputPayload),
                          'in-' + step.id
                        )
                      "
                    >
                      {{ copiedKey === 'in-' + step.id ? 'Copiado' : 'Copiar' }}
                    </action-button>
                  </div>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div
                    v-if="step.inputPayload"
                    class="erp-payload__box"
                    v-html="renderCode(step.inputPayload)"
                  />
                  <p v-else class="erp-payload__empty">
                    Información no disponible
                  </p>
                </div>

                <div class="erp-payload">
                  <div class="erp-payload__head">
                    <span class="erp-payload__label">
                      <em class="fas fa-file-export" />
                      Output Payload
                    </span>
                    <action-button
                      v-if="step.outputPayload"
                      class="erp-copy-btn"
                      :class="{ 'is-copied': copiedKey === 'out-' + step.id }"
                      :i="
                        copiedKey === 'out-' + step.id
                          ? 'fas fa-check'
                          : 'fas fa-copy'
                      "
                      :variant="
                        copiedKey === 'out-' + step.id
                          ? 'success'
                          : 'outline-success'
                      "
                      @click="
                        copyText(
                          formatPayload(step.outputPayload),
                          'out-' + step.id
                        )
                      "
                    >
                      {{
                        copiedKey === 'out-' + step.id ? 'Copiado' : 'Copiar'
                      }}
                    </action-button>
                  </div>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div
                    v-if="step.outputPayload"
                    class="erp-payload__box"
                    v-html="renderCode(step.outputPayload)"
                  />
                  <p v-else class="erp-payload__empty">
                    Información no disponible
                  </p>
                </div>

                <div class="row erp-step__footer">
                  <div class="col-md-4">
                    <span class="data-label">Inicio:</span>
                    <p class="data mb-0">
                      {{ formatDateTime(step.startedAt) }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <span class="data-label">Fin:</span>
                    <p class="data mb-0">
                      {{
                        step.completedAt
                          ? formatDateTime(step.completedAt)
                          : '—'
                      }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <span class="data-label">Duración:</span>
                    <p class="data mb-0">
                      {{ formatSeconds(step.durationMs) }}
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <pagination
        v-if="showPagination"
        :data="pagination"
        @pagination-go-page="goToPage"
      />
    </div>
    <div v-else-if="searched && !fetchFailed" class="erp-results__empty">
      No se encontraron resultados para los filtros seleccionados.
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import 'moment/locale/es';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import PageHeader from '@/components/common/page/PageHeader';
import Pagination from '@/components/common/Pagination';
import Callout from '@/shared/components/Callout/View';
import {
  CustomDropDown,
  CustomDateRangePicker,
  ActionButton,
  CustomTitleDivider,
} from '@/components/common';
import {
  searchErpIntegrationLog,
  getErpOperations,
  getErpSteps,
} from '@/modules/provider/erpIntegrationLog.api';
import alertMixin from '@/mixins/alert.mixin';

const PAGE_SIZE = 15;

export default {
  name: 'ErpIntegrationLog',
  components: {
    PageHeader,
    Pagination,
    Callout,
    CustomDropDown,
    CustomDateRangePicker,
    ActionButton,
    CustomTitleDivider,
  },
  mixins: [alertMixin],
  data() {
    return {
      page: {
        icon: 'fas fa-file-alt',
        title: 'Log Integración ERP',
        routes: ['Inicio', 'Proveedor', 'Log Integración ERP'],
      },
      operations: [],
      dateRange: [null, null],
      filters: {
        operation: null,
        correlationId: '',
      },
      results: [],
      searched: false,
      fetchFailed: false,
      expanded: {},
      stepsById: {},
      stepsLoading: {},
      stepOpen: {},
      copiedKey: null,
      pagination: {
        currentPage: 1,
        pageCount: 0,
        pageSize: PAGE_SIZE,
        count: 0,
        totalItemCount: 0,
      },
    };
  },
  computed: {
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
  },
  async created() {
    await this.loadOperations();
  },
  beforeDestroy() {
    clearTimeout(this._copyTimer);
  },
  methods: {
    apiError(error, fallback) {
      return (
        (error && error.response && error.response.data
          ? error.response.data.message
          : null) || fallback
      );
    },
    async loadOperations() {
      try {
        this.operations = (await getErpOperations()) || [];
      } catch (error) {
        this.ShowToast(
          null,
          this.apiError(error, 'Error al cargar las operaciones.'),
          'error'
        );
      }
    },
    buildModel(page) {
      const [startDate, endDate] = this.dateRange || [];
      return {
        operation: this.filters.operation ? this.filters.operation.id : null,
        startDate,
        endDate,
        correlationId: (this.filters.correlationId || '').trim(),
        pageIndex: page,
        pageSize: PAGE_SIZE,
      };
    },
    async fetchPage(page) {
      this.fetchFailed = false;
      try {
        const result =
          (await searchErpIntegrationLog(this.buildModel(page))) || {};
        this.results = result.data || [];
        this.pagination = {
          currentPage: result.currentPage || page,
          pageCount: result.pageCount || 0,
          pageSize: PAGE_SIZE,
          count: result.count || this.results.length,
          totalItemCount: result.totalItemCount || 0,
        };
        this.expanded = {};
        this.stepsById = {};
        this.stepsLoading = {};
        this.stepOpen = {};
      } catch (error) {
        this.fetchFailed = true;
        this.ShowToast(
          null,
          this.apiError(error, 'Error al realizar la búsqueda.'),
          'error'
        );
      }
    },
    async search() {
      const [startDate, endDate] = this.dateRange || [];
      const correlationId = (this.filters.correlationId || '').trim();
      const hasCriteria =
        !!this.filters.operation || !!startDate || !!endDate || !!correlationId;
      if (!hasCriteria) {
        this.ShowToast(
          null,
          'Debe ingresar al menos un criterio de búsqueda.',
          'error'
        );
        return;
      }
      this.searched = true;
      await this.fetchPage(1);
    },
    goToPage(page) {
      this.fetchPage(page);
    },
    async toggleSteps(id) {
      const willOpen = !this.expanded[id];
      this.expanded = willOpen ? { [id]: true } : {};
      if (willOpen && this.stepsById[id] === undefined) {
        this.$set(this.stepsLoading, id, true);
        try {
          this.$set(this.stepsById, id, (await getErpSteps(id)) || []);
        } catch (error) {
          this.ShowToast(
            null,
            this.apiError(error, 'Error al cargar los pasos.'),
            'error'
          );
          this.$delete(this.stepsById, id);
        } finally {
          this.$set(this.stepsLoading, id, false);
        }
      }
    },
    isExpanded(id) {
      return !!this.expanded[id];
    },
    stepsFor(id) {
      return this.stepsById[id] || [];
    },
    isLoadingSteps(id) {
      return !!this.stepsLoading[id];
    },
    toggleStep(stepId) {
      this.$set(this.stepOpen, stepId, !this.stepOpen[stepId]);
    },
    collapseBeforeEnter(el) {
      el.style.height = '0';
    },
    collapseEnter(el) {
      el.style.height = `${el.scrollHeight}px`;
    },
    collapseAfterEnter(el) {
      el.style.height = 'auto';
    },
    collapseBeforeLeave(el) {
      el.style.height = `${el.scrollHeight}px`;
    },
    collapseLeave(el) {
      void el.offsetHeight;
      el.style.height = '0';
    },
    isStepOpen(stepId) {
      return !!this.stepOpen[stepId];
    },
    renderCode(payload) {
      return DOMPurify.sanitize(
        marked.parse('```json\n' + this.formatPayload(payload) + '\n```')
      );
    },
    formatDate(value) {
      if (!value) return '—';
      const formatted = moment(value)
        .locale('es')
        .format('DD/MMM./YYYY');
      return formatted.replace(
        /\/([a-zñ])/i,
        (match, letter) => `/${letter.toUpperCase()}`
      );
    },
    formatDuration(ms) {
      if (ms == null) return '—';
      return `${(ms / 1000).toFixed(1)} seg`;
    },
    formatDateTime(value) {
      if (!value) return '—';
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatSeconds(ms) {
      if (ms == null) return '—';
      return `${(ms / 1000).toFixed(1)} s`;
    },
    formatPayload(value) {
      if (!value) return '—';
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    },
    // Strip the OIC integration base URL from the step name, keeping only the path.
    // e.g. "HTTP GET https://.../flows/rest/GPR_INT_.../1.0/mipe/vendorInquiry/?VendorId=1"
    //   -> "HTTP GET mipe/vendorInquiry/?VendorId=1"
    formatStepName(name) {
      if (!name) return '—';
      return name.replace(
        /https?:\/\/[^"\s]*?\/ic\/api\/integration\/v1\/flows\/rest\/[^/"\s]+\/[^/"\s]+\/?/g,
        ''
      );
    },
    copyText(value, key) {
      if (!value || !navigator.clipboard) return;
      navigator.clipboard.writeText(value);
      this.copiedKey = key;
      clearTimeout(this._copyTimer);
      this._copyTimer = setTimeout(() => {
        this.copiedKey = null;
      }, 1500);
    },
    onReturn() {
      this.$router.back();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';

$field-height: 38px;
$app-orange: map-get($colors, 'orange');

.erp-search-bar {
  background: map-get($colors, 'gray-9') 0% 0% no-repeat padding-box;
  border-radius: 6px;
  padding: 12px;
  gap: 12px;
  flex-wrap: wrap;
}
.erp-search-bar__field {
  position: relative;
  flex: 1 1 0;
  min-width: 200px;
}
.erp-search-bar__icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: $app-orange;
  z-index: 2;
  pointer-events: none;
}
.erp-search-bar__control {
  height: $field-height;
  padding-left: 34px;
  border: 1px solid #c5cdcf;
  border-radius: 4px;
  background-color: #fff;
}
.erp-search-bar__control::placeholder {
  color: #495057;
  opacity: 0.7;
}
.erp-search-bar__action {
  flex: 0 0 auto;
}
.erp-search-bar__btn {
  height: $field-height;
  display: flex;
  align-items: center;
}

/* Operación dropdown alignment */
.erp-search-bar__field ::v-deep .custom-ddl .vs__dropdown-toggle {
  height: $field-height;
  background-color: #fff;
}

/* ---- Results ---- */
.erp-results {
  margin-top: 8px;
}
/* Square the callout bottom so the message banner sits flush beneath it */
.erp-item--banner ::v-deep .border-radius--light {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.erp-message {
  background-color: #ebf1ee;
  border-top: none;
  border-radius: 0 0 5px 5px;
}
.erp-results__empty {
  text-align: center;
  color: #7f8c8d;
  padding: 24px 0;
}

/* ---- Steps (Ver Pasos) ---- */
.erp-steps {
  margin-top: -1px;
  padding: 16px;
  background-color: #fff;
  border: 1px solid map-get($colors, 'gray-5');
  border-top: none;
  border-radius: 0 0 5px 5px;
}
.erp-steps__header {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: map-get($colors, 'black');
  margin-bottom: 16px;
}
.erp-steps__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 12px;
  border-radius: 50%;
  background-color: $app-orange;
  color: #fff;
  font-size: 12px;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}
.erp-step {
  background-color: #fafbfb;
  border: 1px solid map-get($colors, 'gray-5');
  border-radius: 6px;
  margin-bottom: 10px;
}
.erp-step:last-child {
  margin-bottom: 0;
}
.erp-step__title {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.erp-step__title:focus {
  outline: none;
}
.erp-step__chevron {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 14px;
  border-radius: 50%;
  background-color: #57bd85;
  color: #fff;
  font-size: 14px;
  border: 3px solid #fff;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.18);
}
.erp-step__paso {
  color: map-get($colors, 'green');
  font-weight: 700;
  margin-right: 8px;
}
.erp-step__name {
  font-weight: 700;
  color: map-get($colors, 'black');
}
.erp-step__body {
  padding: 4px 16px 16px;
  overflow: hidden;
}
.erp-collapse-enter-active,
.erp-collapse-leave-active {
  transition: height 0.25s ease;
  overflow: hidden;
}
.erp-payload {
  margin-bottom: 16px;
}
.erp-payload__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.erp-payload__label {
  font-weight: 600;
  color: map-get($colors, 'gray-7');
}
.erp-payload__label em {
  color: $app-orange;
  margin-right: 6px;
}
.erp-payload__box {
  background-color: map-get($colors, 'gray-1');
  border: 1px solid #e6ebe8;
  border-radius: 4px;
  overflow: auto;
}
.erp-payload__box ::v-deep pre {
  margin: 0;
  padding: 12px 14px;
  background: transparent;
  max-height: 260px;
  overflow: auto;
}
.erp-payload__box ::v-deep code {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12.5px;
  color: #2f3b3f;
  white-space: pre-wrap;
  word-break: break-word;
}
.erp-payload__empty {
  margin: 0;
  padding: 12px 14px;
  background-color: map-get($colors, 'gray-1');
  border: 1px solid #e6ebe8;
  border-radius: 4px;
  color: #7f8c8d;
  font-style: italic;
}
.erp-step__footer {
  border-top: 1px solid #eef1f0;
  padding-top: 12px;
}
.erp-copy-btn.is-copied {
  animation: erp-copy-pop 0.35s ease-out;
}
@keyframes erp-copy-pop {
  from {
    transform: scale(1.06);
  }
  to {
    transform: scale(1);
  }
}
</style>
